import type { z } from "zod";
import {
  AiToolkitToolName,
  type AiToolkitToolInputByName,
  type AiToolkitToolOutput,
  type AiToolkitToolOutputByName,
} from "../contract";
import { AiToolkitInvalidInputError, AiToolkitUnknownToolError } from "../errors";

export type AiToolkitToolExecuteArgs<TInput, THostArgs extends object> = THostArgs & {
  input: TInput;
  signal?: AbortSignal;
};

export type AiToolkitToolDefinition<
  TName extends AiToolkitToolName,
  TInput,
  TOutput,
  THostArgs extends object,
> = {
  name: TName;
  inputSchema: z.ZodType<TInput>;
  outputSchema: z.ZodType<TOutput>;
  strict: true;
  execute: (args: AiToolkitToolExecuteArgs<TInput, THostArgs>) => Promise<TOutput>;
};

export type AiToolkitToolsByName<THostArgs extends object> = {
  [TName in AiToolkitToolName]: AiToolkitToolDefinition<
    TName,
    AiToolkitToolInputByName[TName],
    AiToolkitToolOutputByName[TName],
    THostArgs
  >;
};

export type ExecuteAiToolkitToolArgs<THostArgs extends object> = {
  toolsByName: AiToolkitToolsByName<THostArgs>;
  executeArgs: THostArgs;
  toolName: string;
  input: unknown;
  signal?: AbortSignal;
};

const AI_TOOLKIT_TOOL_NAMES = new Set<string>(Object.values(AiToolkitToolName));

const isAiToolkitToolNameValue = (toolName: string): toolName is AiToolkitToolName =>
  AI_TOOLKIT_TOOL_NAMES.has(toolName);

export const isAiToolkitToolName = ({ toolName }: { toolName: string }): boolean =>
  isAiToolkitToolNameValue(toolName);

export const formatAiToolkitZodError = ({ error }: { error: z.ZodError }): string =>
  error.issues.map((issue) => `${issue.path.join(".") || "input"}: ${issue.message}`).join("; ");

const executeToolDefinition = async <
  THostArgs extends object,
  TToolName extends AiToolkitToolName,
>({
  toolDefinition,
  executeArgs,
  toolName,
  input,
  signal,
}: {
  toolDefinition: AiToolkitToolsByName<THostArgs>[TToolName];
  executeArgs: THostArgs;
  toolName: TToolName;
  input: unknown;
  signal?: AbortSignal;
}): Promise<AiToolkitToolOutputByName[TToolName]> => {
  const parsedInput = toolDefinition.inputSchema.safeParse(input);

  if (!parsedInput.success) {
    throw new AiToolkitInvalidInputError({
      toolName,
      message: formatAiToolkitZodError({ error: parsedInput.error }),
    });
  }

  return toolDefinition.execute({
    ...executeArgs,
    input: parsedInput.data,
    signal,
  });
};

export const executeAiToolkitTool = async <THostArgs extends object>({
  toolsByName,
  executeArgs,
  toolName,
  input,
  signal,
}: ExecuteAiToolkitToolArgs<THostArgs>): Promise<AiToolkitToolOutput> => {
  if (!isAiToolkitToolNameValue(toolName)) {
    throw new AiToolkitUnknownToolError({ toolName });
  }

  return executeToolDefinition({
    toolDefinition: toolsByName[toolName],
    executeArgs,
    toolName,
    input,
    signal,
  });
};
